import React, { useState, useEffect } from 'react';
import CMSheader from '../../components/CMSHeader';
import '../../ArticleUpload.scss';
import '../../CMSstyle.scss';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ArticleUpload = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    image_url: '',
    tags: '',
    author: '',
    publish_date: new Date().toISOString().split('T')[0],
    status: 'draft'
  });
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const username = user?.name || "Admin";

  useEffect(() => {
    if (activeTab === 'manage') {
      fetchArticles();
    }
  }, [activeTab]);

  const fetchArticles = async () => {
    try {
      const response = await fetch('/php-api/get_articles.php');
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error('Server returned non-JSON response. Check if PHP API is working correctly.');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setArticles(data.articles);
      } else {
        throw new Error(data.message || 'Failed to fetch articles');
      }
    } catch (error) {
      console.error('Fetch articles error:', error);
      setMessage('Error fetching articles: ' + error.message);
      setIsError(true);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/adminlogin", { replace: true });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    setUploading(true);
    try {
      const response = await fetch('/php-api/uploadarticle_image.php', {
        method: 'POST',
        body: uploadFormData
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error('Server did not return JSON: ' + text.substring(0, 100));
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }
      
      if (data.success) {
        setFormData(prev => ({ ...prev, image_url: data.image_url }));
        setMessage('Image uploaded successfully!');
        setIsError(false);
      } else {
        throw new Error(data.message || 'Failed to upload image');
      }
    } catch (error) {
      setMessage('Error uploading image: ' + error.message);
      setIsError(true);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      setMessage('Title and content are required');
      setIsError(true);
      return;
    }

    setUploading(true);
    try {
      const url = editingArticle 
        ? '/php-api/update_article.php' 
        : '/php-api/create_article.php';
        
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingArticle ? {...formData, id: editingArticle.id} : formData)
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error('Server returned non-JSON response: ' + text.substring(0, 100));
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }
      
      if (data.success) {
        const action = editingArticle ? 'updated' : 'created';
        setMessage(`Article "${formData.title}" ${action} successfully!`);
        setIsError(false);
        
        if (!editingArticle) {
          // Reset form only for new articles
          setFormData({
            title: '',
            content: '',
            category: '',
            image_url: '',
            tags: '',
            author: '',
            publish_date: new Date().toISOString().split('T')[0],
            status: 'draft'
          });
        }
        
        setEditingArticle(null);
        fetchArticles();
      } else {
        throw new Error(data.message || 'Failed to process article');
      }
    } catch (error) {
      setMessage('Error processing article: ' + error.message);
      setIsError(true);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      content: article.content,
      category: article.category || '',
      image_url: article.image_url || '',
      tags: article.tags || '',
      author: article.author || '',
      publish_date: article.publish_date ? article.publish_date.split(' ')[0] : new Date().toISOString().split('T')[0],
      status: article.status || 'draft'
    });
    setActiveTab('upload');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      const response = await fetch('/php-api/delete_article.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error('Server returned non-JSON response: ' + text.substring(0, 100));
      }

      const data = await response.json();
      
      if (data.success) {
        setMessage('Article deleted successfully!');
        setIsError(false);
        fetchArticles();
      } else {
        throw new Error(data.message || 'Failed to delete article');
      }
    } catch (error) {
      setMessage('Error deleting article: ' + error.message);
      setIsError(true);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch('/php-api/update_article_status.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, status: newStatus })
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error('Server returned non-JSON response: ' + text.substring(0, 100));
      }

      const data = await response.json();
      
      if (data.success) {
        setMessage(`Article ${newStatus} successfully!`);
        setIsError(false);
        fetchArticles();
      } else {
        throw new Error(data.message || `Failed to ${newStatus} article`);
      }
    } catch (error) {
      setMessage(`Error ${newStatus} article: ` + error.message);
      setIsError(true);
    }
  };

  const cancelEdit = () => {
    setEditingArticle(null);
    setFormData({
      title: '',
      content: '',
      category: '',
      image_url: '',
      tags: '',
      author: '',
      publish_date: new Date().toISOString().split('T')[0],
      status: 'draft'
    });
  };

  return (
    <>
      <CMSheader onLogout={handleLogout} username={username} />
      <div className='cmspage-body-container'>
        <div className="cmsarticlepage">
          <div className="tab-container">
            <div className="tab-buttons">
              <button 
                className={activeTab === 'upload' ? 'active' : ''}
                onClick={() => setActiveTab('upload')}
              >
                {editingArticle ? 'Edit Article' : 'Upload New Article'}
              </button>
              <button 
                className={activeTab === 'manage' ? 'active' : ''}
                onClick={() => setActiveTab('manage')}
              >
                Manage Articles
              </button>
            </div>

            {message && (
              <div className={`message ${isError ? 'error' : 'success'}`}>
                {message}
              </div>
            )}

            {activeTab === 'upload' && (
              <div className="article-upload-container">
                <h1 className="cmspage-title text-center">
                  {editingArticle ? 'Edit Article' : 'Upload New Article'}
                </h1>
                
                {editingArticle && (
                  <div className="edit-notice">
                    <p>You are editing an existing article. <button onClick={cancelEdit}>Cancel edit</button></p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="article-form">
                  <div className="form-group">
                    <label htmlFor="title">Title *</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="content">Content *</label>
                    <textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      rows="10"
                      required
                    ></textarea>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="category">Category</label>
                      <input
                        type="text"
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="author">Author</label>
                      <input
                        type="text"
                        id="author"
                        name="author"
                        value={formData.author}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="tags">Tags (comma-separated)</label>
                      <input
                        type="text"
                        id="tags"
                        name="tags"
                        value={formData.tags}
                        onChange={handleInputChange}
                        placeholder="Business, Records, Achievement"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="publish_date">Publish Date</label>
                      <input
                        type="date"
                        id="publish_date"
                        name="publish_date"
                        value={formData.publish_date}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="image">Article Image</label>
                    <input
                      type="file"
                      id="image"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                    {formData.image_url && (
                      <div className="image-preview">
                        <img src={formData.image_url} alt="Preview" />
                        <span>Current image: {formData.image_url}</span>
                      </div>
                    )}
                  </div>
                  
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={uploading}
                  >
                    {uploading 
                      ? 'Processing...' 
                      : editingArticle 
                        ? 'Update Article' 
                        : 'Publish Article'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'manage' && (
              <div className="articles-management-container mb-7">
                <h1 className="cmspage-title text-center">Manage Articles</h1>
                
                {articles.length === 0 ? (
                  <p className="no-articles">No articles found.</p>
                ) : (
                  // <div className="table-container">
                  //   <table className="articles-table">
                  //     <thead>
                  //       <tr>
                  //         <th>Title</th>
                  //         <th>Content</th>
                  //         <th>Category</th>
                  //         <th>Author</th>
                  //         <th>Image</th>
                  //         <th>Publish Date</th>
                  //         <th>Status</th>
                  //         <th>Actions</th>
                  //       </tr>
                  //     </thead>
                  //     <tbody>
                  //       {articles.map(article => (
                  //         <tr key={article.id}>
                  //           <td >{article.title}</td>
                  //           <td className="content-cell">{article.content}</td>
                  //           <td>{article.category || '-'}</td>
                  //           <td>{article.author || '-'}</td>
                  //            <td className="content-cell">{article.image_url}</td>
                  //           <td>{article.publish_date ? new Date(article.publish_date).toLocaleDateString() : '-'}</td>
                  //           <td>
                  //             <span className={`status-badge ${article.status}`}>
                  //               {article.status}
                  //             </span>
                  //           </td>
                  //           <td className="actions">
                  //             <button 
                  //               className="btn-edit"
                  //               onClick={() => handleEdit(article)}
                  //               title="Edit"
                  //             >
                  //               Edit
                  //             </button>
                  //             <button 
                  //               className="btn-delete"
                  //               onClick={() => handleDelete(article.id)}
                  //               title="Delete"
                  //             >
                  //               Delete
                  //             </button>
                  //             {article.status === 'draft' && (
                  //               <button 
                  //                 className="btn-publish"
                  //                 onClick={() => handleStatusChange(article.id, 'published')}
                  //                 title="Publish"
                  //               >
                  //                 Publish
                  //               </button>
                  //             )}
                  //             {article.status === 'published' && (
                  //               <button 
                  //                 className="btn-draft"
                  //                 onClick={() => handleStatusChange(article.id, 'draft')}
                  //                 title="Move to Draft"
                  //               >
                  //                 Draft
                  //               </button>
                  //             )}
                  //           </td>
                  //         </tr>
                  //       ))}
                  //     </tbody>
                  //   </table>
                  // </div>
                  <div className="overflow-x-auto">
  <table className="min-w-full border border-gray-200 rounded-lg shadow-sm">
    <thead className="bg-gray-100">
      <tr>
       <th className="px-4 py-2 text-left text-sm font-bold text-gray-700 w-[250px]">Title</th>
        <th className="px-4 py-2 text-left text-sm font-bold text-gray-700 w-[250px]">Content</th>
        <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Category</th>
        <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Author</th>
        <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Image</th>
        <th className="px-4 py-2 text-left text-sm font-bold text-gray-700">Publish Date</th>
        <th className="px-4 py-2 text-center text-sm font-bold text-gray-700">Status</th>
        <th className="px-4 py-2 text-center text-sm font-bold text-gray-700">Actions</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200">
      {articles.map((article) => (
        <tr key={article.id} className="hover:bg-gray-50">
          <td className="px-4 py-2 text-sm font-medium text-gray-900 max-w-[250px] truncate">
  {article.title}
</td>

          {/* Content preview (truncate 2 lines) */}
          <td className="px-4 py-2 text-sm text-gray-700 max-w-[250px]">
            <p className="line-clamp-2">{article.content}</p>
          </td>

          <td className="px-4 py-2 text-sm text-gray-700">{article.category || '-'}</td>
          <td className="px-4 py-2 text-sm text-gray-700">{article.author || '-'}</td>

          {/* Thumbnail instead of URL */}
          <td className="px-4 py-2">
            {article.image_url ? (
              <img
                src={article.image_url}
                alt="Thumbnail"
                className="w-16 h-16 object-cover rounded-md border"
              />
            ) : (
              <span className="text-gray-400 text-sm">No Image</span>
            )}
          </td>

          <td className="px-4 py-2 text-sm text-gray-700">
            {article.publish_date ? new Date(article.publish_date).toLocaleDateString() : '-'}
          </td>

          {/* Status Badge */}
          <td className="px-4 py-2 text-center">
            <span
              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                article.status === 'published'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}
            >
              {article.status}
            </span>
          </td>

          {/* Action buttons */}
          <td className="px-4 py-2">
            <div className="flex gap-2 justify-center items-center"> 
            <button
              onClick={() => handleEdit(article)}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(article.id)}
              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
            >
              Delete
            </button>
            {article.status === 'draft' ? (
              <button
                onClick={() => handleStatusChange(article.id, 'published')}
                className="px-2 py-1 text-xs w-[57px] bg-green-500 text-white rounded hover:bg-green-600"
              >
                Publish
              </button>
            ) : (
              <button
                onClick={() => handleStatusChange(article.id, 'draft')}
                className="px-2 py-1 text-xs w-[57px] bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Draft
              </button>
             
            )}
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticleUpload;